import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { t } from "i18next";

import { RiskDot } from "src/core/components/pdf/shared/RiskDot";
import { FolderStakeholderType } from "src/core/components/pdf/shared/types";
import {
  getStakeholderAlerts,
  getStakeholderFieldValue,
} from "src/core/components/pdf/shared/utils";
import { EReputationAnalysisStatus } from "src/core/constants/eReputation";
import { IRiskOption, ISelectOption } from "src/core/constants/options";
import { CustomRiskLevel } from "src/core/constants/risk";
import { formatIsoToDate } from "src/core/utils/date";

const styles = StyleSheet.create({
  stakeholderBlock: {
    marginBottom: 12,
    backgroundColor: "#FDFDFE",
    borderWidth: 1,
    // équivalent à #0A1A531A
    borderColor: "#E6E8ED",
    borderRadius: 12,
    padding: 12,
  },
  stakeholderHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  stakeholderName: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#0A1A53",
    flex: 1,
  },
  stakeholderBadges: {
    flexDirection: "row",
    gap: 6,
  },
  stakeholderBadgeAlert: {
    backgroundColor: "#FE6447",
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  stakeholderBadgeNoAlert: {
    backgroundColor: "#00D6A8",
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  stakeholderBadgeText: {
    fontSize: 9,
    color: "#FFFFFF",
  },
  stakeholderRoleBadge: {
    backgroundColor: "#F6F7FA",
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  stakeholderRoleBadgeText: {
    fontSize: 9,
    color: "#000000",
  },
  stakeholderInfoRow: {
    flexDirection: "row",
    marginTop: 6,
    alignItems: "center",
  },
  stakeholderInfoLabel: {
    width: "30%",
    fontSize: 10,
    color: "#0A1A53",
    alignItems: "center",
  },
  stakeholderInfoValue: {
    width: "70%",
    fontSize: 10,
    color: "#0A1A53",
    alignItems: "center",
  },
  stakeholderInfoValueWithRisk: {
    width: "70%",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  stakeholderInfoValueText: {
    fontSize: 10,
    color: "#0A1A53",
    alignItems: "center",
  },
  stakeholderDocumentsSection: {},
  stakeholderDocumentRow: {
    flexDirection: "row",
    marginTop: 6,
    alignItems: "center",
  },
  stakeholderDocumentTag: {
    width: "30%",
    paddingRight: 8,
  },
  stakeholderDocumentStatus: {
    width: "70%",
    fontSize: 10,
    color: "#0A1A53",
  },
  documentTag: {
    backgroundColor: "#F6F7FA",
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
    width: "90%",
  },
  documentTagText: {
    fontSize: 9,
    color: "#0A1A53",
  },
});

type StakeholderProps = {
  stakeholder: FolderStakeholderType;
  activityWithRiskOptions: IRiskOption[];
  countryWithRiskOptions: IRiskOption[];
  legalTypesWithRiskOptions: IRiskOption[];
  legalStatusOptions: ISelectOption[];
};

export const Stakeholder = ({
  stakeholder,
  activityWithRiskOptions,
  countryWithRiskOptions,
  legalTypesWithRiskOptions,
  legalStatusOptions,
}: StakeholderProps) => {
  const isCorporation = !!stakeholder.isFolderCustomer;

  const inputValues = stakeholder.inputValues;

  const isPerson = !stakeholder.isFolderCustomer;

  let displayName = "";
  if (isCorporation) {
    displayName = getStakeholderFieldValue(inputValues, "name");
  } else if (isPerson) {
    const firstName = getStakeholderFieldValue(inputValues, "firstName");
    const lastName = getStakeholderFieldValue(inputValues, "lastName");
    displayName = [firstName, lastName].filter(Boolean).join(" ");
  }

  return (
    <View key={stakeholder.entity.id} style={styles.stakeholderBlock}>
      {isCorporation ? (
        <Corporation
          stakeholder={stakeholder}
          activityWithRiskOptions={activityWithRiskOptions}
          countryWithRiskOptions={countryWithRiskOptions}
          legalTypesWithRiskOptions={legalTypesWithRiskOptions}
          legalStatusOptions={legalStatusOptions}
        />
      ) : (
        (() => {
          const alerts = getStakeholderAlerts(stakeholder);

          return (
            <>
              <View style={styles.stakeholderHeader}>
                <Text style={styles.stakeholderName}>{displayName}</Text>
                {isPerson && (alerts.isPepHit || alerts.isSanctionHit) && (
                  <View style={styles.stakeholderBadges}>
                    <View
                      style={
                        (alerts.isPepHit || alerts.isSanctionHit) &&
                        (alerts.pepStatus === EReputationAnalysisStatus.X ||
                          alerts.sanctionStatus === EReputationAnalysisStatus.X)
                          ? styles.stakeholderBadgeAlert
                          : styles.stakeholderBadgeNoAlert
                      }
                    >
                      <Text style={styles.stakeholderBadgeText}>
                        {alerts.pepStatus === EReputationAnalysisStatus.X &&
                        alerts.sanctionStatus === EReputationAnalysisStatus.X
                          ? t("pdf.sections.stakeholderAlerts.pepAndSanction")
                          : alerts.pepStatus === EReputationAnalysisStatus.X &&
                            alerts.sanctionStatus !== EReputationAnalysisStatus.X
                          ? t("pdf.sections.stakeholderAlerts.pep")
                          : alerts.sanctionStatus === EReputationAnalysisStatus.X &&
                            alerts.pepStatus !== EReputationAnalysisStatus.X
                          ? t("pdf.sections.stakeholderAlerts.sanction")
                          : t("pdf.sections.stakeholderAlerts.falsePositive")}
                      </Text>
                    </View>
                  </View>
                )}
              </View>

              <Person stakeholder={stakeholder} countryWithRiskOptions={countryWithRiskOptions} />
            </>
          );
        })()
      )}

      <StakeholderDocuments stakeholder={stakeholder} />
    </View>
  );
};

type CorporationProps = {
  stakeholder: FolderStakeholderType;
  activityWithRiskOptions: IRiskOption[];
  countryWithRiskOptions: IRiskOption[];
  legalTypesWithRiskOptions: IRiskOption[];
  legalStatusOptions: ISelectOption[];
};

export const Corporation = ({
  stakeholder,
  activityWithRiskOptions,
  countryWithRiskOptions,
  legalTypesWithRiskOptions,
  legalStatusOptions,
}: CorporationProps) => {
  const inputValues = stakeholder.inputValues;

  return (
    <>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.corporation.name")}</Text>
        <Text style={styles.stakeholderInfoValue}>
          {getStakeholderFieldValue(inputValues, "name")}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.corporation.activity")}</Text>
        <View style={styles.stakeholderInfoValueWithRisk}>
          {(() => {
            const activityCode = getStakeholderFieldValue(inputValues, "activityCode");
            const activityMatch = activityWithRiskOptions.find((a) => a.value === activityCode);
            const riskLevel = activityMatch?.risk ?? CustomRiskLevel.UNDEFINED;
            return (
              <>
                <RiskDot riskLevel={riskLevel} />
                <Text style={styles.stakeholderInfoValueText}>
                  {activityMatch?.label ?? activityCode}
                </Text>
              </>
            );
          })()}
        </View>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>
          {t("pdf.sections.corporation.registrationCountryCode")}
        </Text>
        <Text style={styles.stakeholderInfoValue}>
          {(() => {
            const countryCode = getStakeholderFieldValue(inputValues, "registrationCountryCode");
            const countryMatch = countryWithRiskOptions.find((c) => c.value === countryCode);
            return countryMatch?.label ?? countryCode;
          })()}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>
          {t("pdf.sections.corporation.registration")}
        </Text>
        <Text style={styles.stakeholderInfoValue}>
          {getStakeholderFieldValue(inputValues, "registration")}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>
          {t("pdf.sections.corporation.entityTypeCode")}
        </Text>
        <View style={styles.stakeholderInfoValueWithRisk}>
          {(() => {
            const entityTypeCode = getStakeholderFieldValue(inputValues, "entityTypeCode");
            const legalTypeMatch = legalTypesWithRiskOptions.find(
              (lt) => lt.value === entityTypeCode
            );
            const riskLevel = legalTypeMatch?.risk ?? CustomRiskLevel.UNDEFINED;
            return (
              <>
                <RiskDot riskLevel={riskLevel} />
                <Text style={styles.stakeholderInfoValueText}>
                  {legalTypeMatch?.label ?? entityTypeCode}
                </Text>
              </>
            );
          })()}
        </View>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>
          {t("pdf.sections.corporation.customListCode2")}
        </Text>
        <View style={styles.stakeholderInfoValueWithRisk}>
          {(() => {
            const legalStatusCode = getStakeholderFieldValue(inputValues, "customListCode2");
            const legalStatusMatch = legalStatusOptions.find((ls) => ls.value === legalStatusCode);
            return (
              <Text style={styles.stakeholderInfoValueText}>
                {legalStatusMatch?.label ?? legalStatusCode}
              </Text>
            );
          })()}
        </View>
      </View>
      {getStakeholderFieldValue(inputValues, "customListCode2") === "Autre" && (
        <View style={styles.stakeholderInfoRow}>
          <Text style={styles.stakeholderInfoLabel}>
            {t("pdf.sections.corporation.customString3")}
          </Text>
          <Text style={styles.stakeholderInfoValue}>
            {getStakeholderFieldValue(inputValues, "customString3")}
          </Text>
        </View>
      )}
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>
          {t("pdf.sections.corporation.customString2")}
        </Text>
        <Text style={styles.stakeholderInfoValue}>
          {getStakeholderFieldValue(inputValues, "customString2")}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>
          {t("pdf.sections.corporation.customString1")}
        </Text>
        <Text style={styles.stakeholderInfoValue}>
          {getStakeholderFieldValue(inputValues, "customString1")}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>
          {t("pdf.sections.corporation.customCountryCode1")}
        </Text>
        <Text style={styles.stakeholderInfoValue}>
          {(() => {
            const countryCode = getStakeholderFieldValue(inputValues, "customCountryCode1");
            const countryMatch = countryWithRiskOptions.find((c) => c.value === countryCode);
            return countryMatch?.label ?? countryCode;
          })()}
        </Text>
      </View>
    </>
  );
};

type PersonProps = {
  stakeholder: FolderStakeholderType;
  countryWithRiskOptions: IRiskOption[];
};

export const Person = ({ stakeholder, countryWithRiskOptions }: PersonProps) => {
  const inputValues = stakeholder.inputValues;

  return (
    <>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.person.title")}</Text>
        <Text style={styles.stakeholderInfoValue}>
          {getStakeholderFieldValue(inputValues, "title")}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.person.firstName")}</Text>
        <Text style={styles.stakeholderInfoValue}>
          {getStakeholderFieldValue(inputValues, "firstName")}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.person.birthName")}</Text>
        <Text style={styles.stakeholderInfoValue}>
          {getStakeholderFieldValue(inputValues, "birthName")}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.person.lastName")}</Text>
        <Text style={styles.stakeholderInfoValue}>
          {getStakeholderFieldValue(inputValues, "lastName")}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.person.role.title")}</Text>
        <View style={styles.stakeholderBadges}>
          {stakeholder.isLegalRepresentative && (
            <View style={styles.stakeholderRoleBadge}>
              <Text style={styles.stakeholderRoleBadgeText}>
                {t("pdf.sections.person.role.legalRepresentative")}
              </Text>
            </View>
          )}
          {stakeholder.beneficiaryOwner && (
            <View style={styles.stakeholderRoleBadge}>
              <Text style={styles.stakeholderRoleBadgeText}>
                {t("pdf.sections.person.role.beneficiaryOwner")}
              </Text>
            </View>
          )}
        </View>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>
          {t("pdf.sections.person.citizenshipCountryCode")}
        </Text>
        <Text style={styles.stakeholderInfoValue}>
          {(() => {
            const countryCode = getStakeholderFieldValue(inputValues, "citizenshipCountryCode");
            const countryMatch = countryWithRiskOptions.find((c) => c.value === countryCode);
            return countryMatch?.label ?? countryCode;
          })()}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.person.birthDate")}</Text>
        <Text style={styles.stakeholderInfoValue}>
          {(() => {
            const birthDate = getStakeholderFieldValue(inputValues, "birthDate");
            return birthDate !== "-" ? formatIsoToDate(birthDate) : "-";
          })()}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>
          {t("pdf.sections.person.residenceCountryCode")}
        </Text>
        <View style={styles.stakeholderInfoValueWithRisk}>
          {(() => {
            const countryCode = getStakeholderFieldValue(inputValues, "residenceCountryCode");
            const countryMatch = countryWithRiskOptions.find((c) => c.value === countryCode);
            const riskLevel = countryMatch?.risk ?? CustomRiskLevel.UNDEFINED;
            return (
              <>
                <RiskDot riskLevel={riskLevel} />
                <Text style={styles.stakeholderInfoValueText}>
                  {countryMatch?.label ?? countryCode}
                </Text>
              </>
            );
          })()}
        </View>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.person.birthCountryCode")}</Text>
        <Text style={styles.stakeholderInfoValue}>
          {(() => {
            const countryCode = getStakeholderFieldValue(inputValues, "birthCountryCode");
            const countryMatch = countryWithRiskOptions.find((c) => c.value === countryCode);
            return countryMatch?.label ?? countryCode;
          })()}
        </Text>
      </View>
      <View style={styles.stakeholderInfoRow}>
        <Text style={styles.stakeholderInfoLabel}>{t("pdf.sections.person.birthPlace")}</Text>
        <Text style={styles.stakeholderInfoValue}>
          {getStakeholderFieldValue(inputValues, "birthCityCode")}
        </Text>
      </View>
    </>
  );
};

type StakeholderDocumentsProps = {
  stakeholder: FolderStakeholderType;
};

export const StakeholderDocuments = ({ stakeholder }: StakeholderDocumentsProps) => {
  return (
    <>
      {stakeholder.supportingDocuments && stakeholder.supportingDocuments.length > 0 && (
        <View style={styles.stakeholderDocumentsSection}>
          {stakeholder.supportingDocuments
            .filter((doc) => doc.mainDocument !== null && doc.mainDocument !== undefined)
            .map((doc, docIndex) => {
              const documentTypeName =
                doc.mainDocument?.type?.name ??
                doc.abstractDocumentType?.name ??
                "Type de document non renseigné";
              const isExternalDocument = doc.mainDocument?.__typename === "ExternalDocument";
              const documentStatus = isExternalDocument
                ? t("pdf.sections.stakeholderDocuments.externalDocument")
                : t("pdf.sections.stakeholderDocuments.uploadedDocument");

              return (
                <View key={docIndex} style={styles.stakeholderDocumentRow}>
                  <View style={styles.stakeholderDocumentTag}>
                    <View style={styles.documentTag}>
                      <Text style={styles.documentTagText}>{documentTypeName}</Text>
                    </View>
                  </View>
                  <Text style={styles.stakeholderDocumentStatus}>{documentStatus}</Text>
                </View>
              );
            })}
        </View>
      )}
    </>
  );
};
